import * as Sentry from "@sentry/react";

import { Form, FormikProps } from "formik";

import { ProductPriceTile } from "Components/Features/CheckPrice/Tiles/ProductPriceTile";
import { SystemErrorAlert } from "Components/Form/Alert/SystemErrorAlert";
import { NavigationButtons } from "Components/NavigationButtons";
import { useTexts } from "Components/TextContext";
import { useAddProductToCartHandler } from "Store/addProductToCart/hooks";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { HealthCoverage } from "Store/interfaces/products/coverage";
import { useFormState } from "Store/priceCalculation/priceCalculationHooks";
import { ReduxState } from "Store/rootReducer";
import { isDefined } from "Utils/functions";
import React from "react";
import { useSelector } from "react-redux";
import { HEALTH_PRICE_STEP } from "../healthSteps";
import { HealthCoverages } from "./HealthCoverages";
import { HealthProductPriceFormValues } from "./healthProductPriceInterfaces";
import { validationSchema } from "./healthProductPriceValidation";

export const HealthProductPrice = (): React.ReactElement => {
  const addProductToCart = useAddProductToCartHandler(HEALTH_PRICE_STEP.id);
  const initialFormState = useFormState<HealthProductPriceFormValues>(
    HEALTH_PRICE_STEP.id,
    { coverage: HealthCoverage.HEALTH_PLUS },
  );
  const { getText } = useTexts();
  const { priceCalculation, addProductToCart: submitState } = useSelector(
    (state: ReduxState) => state,
  );

  const handleSubmit = (formValues: HealthProductPriceFormValues): void => {
    const productToAdd = {
      coverage: formValues.coverage as HealthCoverage,
      productType: INSURANCE_NAME_VALUE.HEALTH_WITHOUT_SIGNING,
    };
    addProductToCart(formValues, [productToAdd]);
  };

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag(
          "location",
          `CheckPrice - ProductPrice - ${INSURANCE_NAME_VALUE.HEALTH_WITHOUT_SIGNING}`,
        );
      }}
    >
      <ProductPriceTile
        header={getText("healthform.pageheader.checkprice")}
        initialValues={initialFormState}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {(formProps: FormikProps<HealthProductPriceFormValues>) => (
          <Form>
            <HealthCoverages
              formProps={formProps}
              isPriceLoading={priceCalculation.priceCalculationResult.isLoading}
            />

            {isDefined(submitState.error) && (
              <SystemErrorAlert alertId="addProductToCart" />
            )}

            <NavigationButtons isUpsell isLoading={submitState.isLoading} />
          </Form>
        )}
      </ProductPriceTile>
    </Sentry.ErrorBoundary>
  );
};
